import { Injectable } from '@angular/core';
import { Firestore, collection, doc, getCountFromServer, query, where, orderBy, limit as firestoreLimit, collectionData, docData, getDoc, deleteDoc } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Job } from '../models/job.interface';
import { Image } from '../models/image.interface';
import { User } from '../models/user.interface';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  constructor(private firestore: Firestore) {}

  getJobList(uid: string): Observable<Job[]> {
    const jobsRef = collection(this.firestore, 'ge_users', uid, 'jobs');
    return collectionData(jobsRef, { idField: 'id' }) as Observable<Job[]>;
  }

  getJobDetail(uid: string, id: string): Observable<Job> {
    const jobDoc = doc(this.firestore, `/ge_users/${uid}/jobs/${id}`);
    return docData(jobDoc, { idField: 'id' }) as Observable<Job>;
  }

  async deleteJob(uid: string, id: string): Promise<void> {
    const userJobRef = doc(this.firestore, '/ge_users/' + uid + '/jobs/' + id);
    const globalJobRef = doc(this.firestore, '/ge_jobs/' + id);
    await deleteDoc(userJobRef);
    return await deleteDoc(globalJobRef);
  }

  getJobGallery(uid: string, id: string): Observable<Image[]> {
    const imagesRef = collection(this.firestore, '/ge_users/' + uid + '/jobs/' + id + '/images/');
    const q = query(
      imagesRef,
      orderBy('index', 'desc'),
      firestoreLimit(5)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Image[]>;
  }

  async countClassifiedJobs(classification: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async newClassifiedJobs(classification: string, country: string): Promise<number> {
    const today = Date.now();
    const month = today - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countGlobalJobs(classification: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async newGlobalJobs(classification: string): Promise<number> {
    const today = Date.now();
    const month = today - 30 * 24 * 60 * 60 * 1000;
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      where('publish', '>=', month)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countIndustryJobs(grouping: string, country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countCountryJobs(country: string): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('location', '==', 'onsite'),
      where('country', '==', country),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countRemoteJobs(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  async countJobs(): Promise<number> {
    const collectionRef = collection(this.firestore, 'ge_jobs');
    const countQuery = query(
      collectionRef,
      where('visibility', '==', true)
    );
    const count = await getCountFromServer(countQuery);
    return count.data().count;
  }

  getCountryJobs(country: string, limitValue: number): Observable<Job[]> {
    const jobCollectionRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobCollectionRef,
      where('country', '==', country),
      where('location', '==', 'onsite'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getLocalJobs(location: string, country: string, limitValue: number): Observable<Job[]> {
    const jobCollectionRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobCollectionRef,
      where('country', '==', country),
      where('cities', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getRegionJobs(location: string, country: string, limitValue: number): Observable<Job[]> {
    const q = query(
      collection(this.firestore, 'ge_jobs'),
      where('country', '==', country),
      where('regions', 'array-contains', location),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getClassifiedJobs(classification: string, country: string, limitValue: number): Observable<Job[]> {
    const q = query(
      collection(this.firestore, 'ge_jobs'),
      where('classification', 'array-contains', classification),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getAllClassifiedJobs(classification: string, limitValue: number): Observable<Job[]> {
    const jobCollectionRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobCollectionRef,
      where('classification', 'array-contains', classification),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getIndustryJobs(grouping: string, country: string, limitValue: number): Observable<Job[]> {
    const q = query(
      collection(this.firestore, 'ge_jobs'),
      where('grouping', 'array-contains', grouping),
      where('country', '==', country),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getAllIndustryJobs(grouping: string, limitValue: number): Observable<Job[]> {
    const jobsRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobsRef,
      where('grouping', 'array-contains', grouping),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getRemoteJobs(limitValue: number) {
    const jobCollectionRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobCollectionRef,
      where('location', '==', 'remote'),
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(limitValue)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  jobs() {
    const jobCollectionRef = collection(this.firestore, 'ge_jobs');
    const q = query(jobCollectionRef,
      where('visibility', '==', true),
      orderBy('unpublish', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  getProfile(uid: string) {
    const userRef = doc(this.firestore, `ge_users/${uid}`);
    return getDoc(userRef);
  }

  jobCard(id: string) {
    const jobRef = doc(this.firestore, `ge_jobs/${id}`);
    return getDoc(jobRef);
  }

  jobDetail(id: string): Observable<Job> {
    const jobDoc = doc(this.firestore, `ge_jobs/${id}`);
    return docData(jobDoc, { idField: 'id' }) as Observable<Job>;
  }

  jobDetails(slug: string): Observable<Job[]> {
    const q = query(
      collection(this.firestore, 'ge_jobs'),
      where('slug', '==', slug)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Job[]>;
  }

  jobHunters(id: string): Observable<User[]> {
    const huntersRef = collection(this.firestore, `ge_jobs/${id}/hunters`);
    const q = query(
      huntersRef,
      orderBy('applied', 'desc'),
      firestoreLimit(10)
    );
    return collectionData(q, { idField: 'id' }) as Observable<User[]>;
  }
}
